import React, { FC, useMemo } from 'react';
import CourseTypeBadge from '../../atoms/course-type-badge/course-type-badge';
import CountryPill from '../../atoms/country-pill/country-pill';
import { IUniversityData } from '../../../models/university/IUniversityData';
import { CourseType } from '../../../constants/course-type';
import Link from 'next/link';
import moduleStyles from './university-card.module.scss';
import { getHeapAttributes } from '../../../utils/heap/heap.helper';

export type Props = {
  university: IUniversityData;
  getLink: (university: IUniversityData) => string;
  showEntryRoutePills: boolean;
  showIntegratedMastersLabel?: boolean;
  showICASlabel?: boolean;
};

function imageParams(src: string) {
  return `${src}?h=${640}&fit=pad`;
}

const CourseTypeItem: FC<{ courseType: CourseType; showIntegratedMastersLabel: boolean }> = ({
  courseType,
  showIntegratedMastersLabel,
}) => {
  if (!courseType) {
    return null;
  }

  return (
    <li>
      <CourseTypeBadge
        type={courseType}
        thin
        showIntegratedMastersLabel={showIntegratedMastersLabel}
      />
    </li>
  );
};

export default function UniversityCard({
  university,
  getLink,
  showEntryRoutePills,
  showIntegratedMastersLabel,
  showICASlabel,
}: Props) {
  const isOpenInNewTab = university?.clavelUrl ? true : false;
  const renderData = useMemo(() => {
    const topBorderStyling = {
      backgroundColor: university?.brand?.primaryColour,
    };
    if (showICASlabel === false && university.entryRoutes.find((e) => e === CourseType.ICAS)) {
      university.entryRoutes.splice(
        university.entryRoutes.findIndex((e) => e === CourseType.ICAS),
        1,
      );
    }

    const universityCardLink = getLink(university);

    const heapAttributes = getHeapAttributes(`university-card`, { name: university.name });

    return { topBorderStyling, universityCardLink, heapAttributes };
  }, [university]);

  return (
    <Link
      href={renderData.universityCardLink}
      target={isOpenInNewTab ? '_blank' : '_self'}
      id={`${university.slug}-card`}
      className={moduleStyles.uni_card_link}
      aria-label={`get more details about ${university.name}`}
      {...renderData.heapAttributes}
    >
      <div className={`${moduleStyles.uni_card_top}`} style={renderData.topBorderStyling} />
      <div className={`card ${moduleStyles.uni_card}`}>
        <div className={`${moduleStyles.uni_card_img_container}`}>
          {university.brand?.logo && (
            <img
              src={imageParams(university.brand?.logo)}
              alt={`${university?.name} logo`}
              className={`${moduleStyles.uni_card_img}`}
            />
          )}
        </div>
        <div className={`${moduleStyles.uni_card_container}`}>
          <h5 className={moduleStyles.uni_card_name}>{university.name}</h5>
          <p className={`${moduleStyles.uni_card_tagline}`}>{university.tagline}</p>
          <div className='align-items-end d-flex justify-content-between mt-auto'>
            <div className={`${moduleStyles.uni_card_footer}`}>
              {showEntryRoutePills && university.entryRoutes?.length > 0 && (
                <>
                  <p className='visually-hidden'>Course types offered by this university:</p>
                  <ul className={`${moduleStyles.uni_card_course_types}`}>
                    {university.entryRoutes.map((courseType: CourseType) => (
                      <CourseTypeItem
                        key={`${university.id}-${courseType}`}
                        courseType={courseType}
                        showIntegratedMastersLabel={showIntegratedMastersLabel}
                      />
                    ))}
                  </ul>
                </>
              )}
            </div>
            {university.country?.shortCountryName && (
              <div className={`${moduleStyles.uni_card_pill_wrapper}`}>
                <CountryPill country={university.country} />
              </div>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
}
